import { LitElement, html, css, PropertyValues, unsafeCSS } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { styleMap } from "lit/directives/style-map.js";
import { classMap } from "lit/directives/class-map.js";
import { when } from "lit/directives/when.js";
import { resolveRadius } from "../helper/utils/size-resolver";
import {
  resolveColor,
  fadeMix,
  hoverShade,
  isDefaultColorToken,
  tuneSurfaceVariantForDefaultColor,
  resolveSemanticSurfaceKey,
  semanticSoftSurfaceTokens,
  skyBorderPrimaryShorthand,
} from "../helper/utils/color-resolver";
import { cssOnFillEnhance, ON_COLOR_FALLBACK_LIGHT } from "../helper/utils/contrast-color";
import { SkyIcon } from "../sky-icon/sky-icon.js";
import { PresetController } from "../helper/controllers/preset-controller";
import { skyFocusRingHostStyles } from "../helper/styles/focus-ring";

export type SkyAlertClosedEventDetail = { title: string; message: string };
export type SkyAlertStrip = "start" | "end" | "both" | "none";
export type SkyAlertVariant = "flat" | "bordered" | "light" | "faded" | "solid";
export type SkyAlertRoleType = "alert" | "alertdialog";
/** Resolved size metrics returned by `SkyAlert._resolveSize()`. */
type SkyAlertResolvedSize = {
  padding: string;
  titleFontSize: string;
  messageFontSize: string;
  gap: string;
  icon: string;
  closeSize: string;
  closeFontSize: string;
  spinner: string;
  stripWidth: string;
  minHeight: string;
};

/**
 * @element sky-alert
 *
 * @summary Status alert banner with variants, optional icon, strip accents, actions, dismiss behavior, and loading state.
 *
 * @status stable
 * @since 1.0.0
 *
 * @documentation https://sky-ui.com/components/alert
 * @dependency sky-icon
 *
 * @slot - Custom message content (used when `message` is empty).
 * @slot actions - Action buttons or links displayed on the trailing side.
 *
 * @property {string} title - Title text shown above the message.
 * @property {string} message - Message text rendered inside the alert.
 * @property {boolean} removable - Shows a dismiss button when enabled.
 * @property {string} color - Base color token or CSS color used by the alert.
 * @property {string} size - Preset size token (`xs | sm | md | lg | xl`) or CSS length.
 * @property {string} radius - Radius token or CSS length.
 * @property {string | null} icon - Optional leading icon name for `<sky-icon>`.
 * @property {SkyAlertStrip} strip - Strip accent placement.
 * @property {string} stripColor - Strip accent color token or CSS color.
 * @property {SkyAlertVariant} variant - Visual style variant.
 * @property {boolean} loading - Shows a loading overlay.
 * @property {SkyAlertRoleType} roleType - ARIA role for the alert container.
 * @property {string} preset - Named prop preset from nearest `sky-config-provider`. Default: `""`.
 * @method close Dispatches `alert-closed` and removes the alert from the DOM.
 *
 * @fires {CustomEvent<SkyAlertClosedEventDetail>} alert-closed - Fired when the alert is dismissed via the close button; bubbles and is composed.
 *
 * @csspart alert - The outer alert container.
 * @csspart icon - The optional leading icon.
 * @csspart content - The content container (title + message/slot).
 * @csspart title - The title text element.
 * @csspart message - The message text element (when `message` is provided).
 * @csspart actions - The trailing actions container.
 * @csspart close-button - The dismiss button (when `removable`).
 * @csspart loading-overlay - Overlay shown when `loading` is true.
 * @csspart loading-spinner - Spinner element shown inside the loading overlay.
 *
 * @Behavior
 * - `message` content takes priority over default-slot message content.
 * - Dismissing a removable alert dispatches `alert-closed` and removes the element.
 * - `strip` controls accent placement (`start`, `end`, `both`, `none`).
 * - `role-type="alert"` maps to `aria-live="assertive"`; `alertdialog` maps to `aria-live="polite"`.
 * - Respects reduced motion preferences for entry, button hover rotation, and spinner animation.
 *
 * @example
 * ```html
 * <sky-alert title="Upload Complete" message="Your file is now available." variant="bordered" strip="start" removable>
 *   <button slot="actions">View</button>
 * </sky-alert>
 * ```
 * ```vue
 * <template>
 *   <sky-alert title="Upload Complete" message="Your file is now available." variant="bordered" strip="start" removable>
 *     <button slot="actions">View</button>
 *   </sky-alert>
 * </template>
 * ```
 * ```jsx
 * export default function Demo() {
 *   return (
 *     <sky-alert title="Upload Complete" message="Your file is now available." variant="bordered" strip="start" removable>
 *       <button slot="actions">View</button>
 *     </sky-alert>
 *   );
 * }
 * ```
 */

@customElement("sky-alert")
export class SkyAlert extends LitElement {

  static dependencies: Record<string, CustomElementConstructor> = {
    "sky-icon": SkyIcon,
  };

  /** Title text displayed above the message. @default "" */
  @property({ type: String }) title: string = "";

  /** Message text displayed inside the alert. @default "" */
  @property({ type: String }) message: string = "";

  /** Shows a close button that dismisses the alert. @default false */
  @property({ type: Boolean }) removable: boolean = false;

  /** Base color token or CSS color. @default "primary" */
  @property({ type: String }) color: string = "primary";

  /** Size token (`xs | sm | md | lg | xl`) or CSS length. @default "md" */
  @property({ type: String }) size: string = "md";

  /** Radius token or CSS length. @default "md" */
  @property({ type: String }) radius: string = "md";

  /** Optional leading icon name for `<sky-icon>`. @default null */
  @property({ type: String }) icon: string | null = null;

  /** Strip accent position. @default "none" */
  @property({ type: String, reflect: true }) strip: SkyAlertStrip = "none";

  /** Strip accent color. @default "currentColor" */
  @property({ type: String })
  stripColor: string = "currentColor";

  /** Visual style variant. @default "solid" */
  @property({ type: String, reflect: true })
  variant: SkyAlertVariant = "solid";

  /** Shows a loading overlay. @default false */
  @property({ type: Boolean }) loading: boolean = false;

  /** ARIA role applied to the alert container. @default "alert" */
  @property({ type: String, reflect: true })
  roleType: SkyAlertRoleType = "alert";

  /** Internal style variables */
  private _styleVars: Record<string, string> = {};

  /** Cached result of _resolveSize for the last `size` value (avoids recomputing when only color/variant change). */
  private _sizeCache: { size: string; result: SkyAlertResolvedSize } | null = null;

  /** True when the actions slot has assigned content (used to show separator only when needed). */
  @state() private _hasActionsSlotContent = false;


  /** Named prop preset from nearest `sky-config-provider`. */
  @property({ type: String, reflect: true }) preset = "";

  private _presets = new PresetController(this);

  static override styles = [
    skyFocusRingHostStyles,
    css`
    :host {
      display: block;
      font-family: var(--sky-font);
      width: 100%;
    }

    .alert {
      position: relative;
      width: 100%;
      box-sizing: border-box;
      display: flex;
      align-items: stretch;
      gap: var(--alert-gap, var(--sky-space-2, 8px));
      padding: var(--alert-padding);
      border-radius: var(--alert-radius);
      border: var(--alert-border, none);
      color: var(--alert-color, var(--sky-text-primary));
      background-color: var(--alert-bg, transparent);
      backdrop-filter: none;
      -webkit-backdrop-filter: none;
      transition: var(--sky-transition-default);
      min-height: var(--alert-min-height, 36px);
      animation: skyc-fade-in var(--sky-duration-fast, 120ms) var(--sky-ease-out, ease-out);
    }

    /* Strip styles (logical properties for RTL) */
    .alert.strip-start::before,
    .alert.strip-end::after {
      content: "";
      position: absolute;
      top: 0;
      bottom: 0;
      width: var(--strip-width, 4px);
      background: var(--strip-color, currentColor);
      z-index: 1;
    }

    .alert.strip-start::before {
      inset-inline-start: 0;
      border-start-start-radius: inherit;
      border-end-start-radius: inherit;
    }

    .alert.strip-end::after {
      inset-inline-end: 0;
      border-start-end-radius: inherit;
      border-end-end-radius: inherit;
    }

    .alert.strip-start {
      border-start-start-radius: 0 !important;
      border-end-start-radius: 0 !important;
    }
    .alert.strip-end {
      border-start-end-radius: 0 !important;
      border-end-end-radius: 0 !important;
    }

    .alert.strip-start.strip-end {
      border-radius: 0 !important;
    }

    /* Remove borders on strip sides */
    .alert.strip-start.bordered {
      border-inline-start: none !important;
    }
    .alert.strip-end.bordered {
      border-inline-end: none !important;
    }
    .alert.strip-start.strip-end.bordered {
      border-inline-start: none !important;
      border-inline-end: none !important;
    }

    .alert.faded.strip-start {
      border-inline-start: none !important;
    }
    .alert.faded.strip-end {
      border-inline-end: none !important;
    }
    .alert.faded.strip-start.strip-end {
      border-inline-start: none !important;
      border-inline-end: none !important;
    }

    .icon {
      align-self: flex-start;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: var(--icon-size, 20px);
      color: inherit;
      flex-shrink: 0;
      padding-block-start: 2px;
    }

    .content {
      flex: 1 1 auto;
      min-width: 0;
      display: flex;
      flex-direction: column;
      justify-content: center;
    }

    .title {
      font: var(--sky-text-title);
      font-size: var(--title-font-size);
      color: inherit;
      margin: 0 0 var(--sky-space-1, 4px) 0;
    }

    .message {
      margin: 0;
      text-align: start;
      font: var(--sky-text-body);
      font-size: var(--message-font-size);
      color: inherit;
    }

    ::slotted(*) {
      font: var(--sky-text-body);
      font-size: var(--message-font-size);
    }

    .actions {
      display: inline-flex;
      gap: var(--sky-space-2, 8px);
      align-items: center;
      justify-content: flex-end;
      flex: 0 0 auto;
      align-self: stretch;
      border-inline-start: 1px solid var(--sky-border-light);
      padding-inline-start: var(--sky-space-2, 8px);
      margin-inline-start: var(--sky-space-1, 4px);
    }

    .actions-slot-placeholder {
      position: absolute;
      width: 0;
      height: 0;
      overflow: hidden;
      opacity: 0;
      pointer-events: none;
    }

    .close-button {
      margin-inline-start: var(--sky-space-1\\.5, 6px);
      display: inline-flex;
      aspect-ratio: 1 / 1;
      justify-content: center;
      align-items: center;
      width: var(--close-size, 24px);
      height: var(--close-size, 24px);
      box-sizing: border-box;
      background: var(--close-bg, var(--sky-hover-primary));
      border: none;
      border-radius: var(--sky-radius-full, 50%);
      font-size: var(--close-font-size, 16px);
      cursor: pointer;
      font-weight: var(--sky-font-weight-bold, 700);
      color: var(--close-color, currentColor);
      transition: var(--sky-transition-fast);
      -webkit-tap-highlight-color: transparent;
    }

    .close-button:hover {
      transform: rotate(90deg);
      background: var(--close-hover-bg, var(--sky-hover-secondary));
      color: var(--close-hover-color, currentColor);
    }

    .close-button:focus-visible {
      outline: none;
      box-shadow: var(--sky-focus-ring);
      background: var(--close-hover-bg, var(--sky-hover-secondary));
      color: var(--close-hover-color, currentColor);
    }

    .loading-overlay {
      position: absolute;
      inset: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      background: var(--sky-overlay-secondary);
      border-radius: inherit;
      backdrop-filter: none;
      -webkit-backdrop-filter: none;
      z-index: 2;
    }

    .loading-spinner {
      width: var(--spinner-size, 16px);
      height: var(--spinner-size, 16px);
      border: 2px solid currentColor;
      border-top-color: transparent;
      border-radius: var(--sky-radius-full, 50%);
      animation: skyc-spin 0.8s linear infinite;
    }

    @media (prefers-reduced-motion: reduce) {
      .alert {
        animation: none;
        transition: none;
      }

      .close-button {
        transition: none;
      }

      .close-button:hover,
      .close-button:focus-visible {
        transform: none;
      }

      .loading-spinner {
        animation: none;
      }
    }

    @keyframes skyc-spin {
      to {
        transform: rotate(360deg);
      }
    }

    @keyframes skyc-fade-in {
      from {
        opacity: 0;
        transform: translateY(-2px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }

    ${unsafeCSS(
      cssOnFillEnhance([
        {
          selector: ".alert.solid",
          fillVar: "--alert-bg",
        },
      ]),
    )}
  `,
  ];

  /**
   * Resolves size to get padding, font sizes, and other metrics.
   * Result is memoized by `size` to avoid recomputing when only color/variant/radius change.
   */
  private _resolveSize(input: string): SkyAlertResolvedSize {
    const key = String(input).trim();
    if (this._sizeCache && this._sizeCache.size === key) return this._sizeCache.result;

    const presets: Record<
      string,
      {
        padding: string;
        titleFontSize: string;
        messageFontSize: string;
        gap: string;
        icon: string;
        closeSize: string;
        closeFontSize: string;
        spinner: string;
        stripWidth: string;
        minHeight: string;
      }
    > = {
      xs: {
        padding: "3px 7px",
        titleFontSize: "12px",
        messageFontSize: "11px",
        gap: "4px",
        icon: "14px",
        closeSize: "18px",
        closeFontSize: "12px",
        spinner: "12px",
        stripWidth: "2px",
        minHeight: "28px",
      },
      sm: {
        padding: "5px 9px",
        titleFontSize: "14px",
        messageFontSize: "13px",
        gap: "6px",
        icon: "16px",
        closeSize: "20px",
        closeFontSize: "14px",
        spinner: "14px",
        stripWidth: "3px",
        minHeight: "32px",
      },
      md: {
        padding: "8px 12px",
        titleFontSize: "16px",
        messageFontSize: "14px",
        gap: "8px",
        icon: "20px",
        closeSize: "24px",
        closeFontSize: "16px",
        spinner: "16px",
        stripWidth: "4px",
        minHeight: "36px",
      },
      lg: {
        padding: "10px 14px",
        titleFontSize: "18px",
        messageFontSize: "15px",
        gap: "10px",
        icon: "22px",
        closeSize: "28px",
        closeFontSize: "18px",
        spinner: "18px",
        stripWidth: "5px",
        minHeight: "40px",
      },
      xl: {
        padding: "12px 16px",
        titleFontSize: "20px",
        messageFontSize: "16px",
        gap: "12px",
        icon: "24px",
        closeSize: "32px",
        closeFontSize: "20px",
        spinner: "20px",
        stripWidth: "6px",
        minHeight: "44px",
      },
    };

    if (presets[input]) {
      this._sizeCache = { size: key, result: presets[input] };
      return presets[input];
    }

    const m = String(input)
      .trim()
      .match(/^([\d.]+)(px|rem|em)?$/i);
    const unit = m?.[2] || "px";
    const fsNum = m ? parseFloat(m[1]) : 16;
    const fs = `${fsNum}${unit}`;

    const to = (n: number) => `${Math.round(n * 100) / 100}${unit}`;
    const clamp = (n: number, min: number, max: number) =>
      Math.max(min, Math.min(max, n));

    const titleFs = fs;
    const messageFs = clamp(fsNum * 0.875, 11, 16);
    const py = clamp(fsNum * 0.5, 3, 12);
    const px = clamp(fsNum * 0.75, 7, 16);
    const gap = clamp(fsNum * 0.5, 4, 12);
    const icon = clamp(fsNum * 1.25, 14, 24);
    const closeSize = clamp(fsNum * 1.5, 18, 32);
    const closeFont = clamp(fsNum * 1, 12, 20);
    const spinner = clamp(fsNum * 1, 12, 20);
    const stripWidth = clamp(fsNum * 0.25, 2, 6);
    const minHeight = clamp(fsNum * 2.25, 28, 44);

    return {
      padding: `${to(py)} ${to(px)}`,
      titleFontSize: titleFs,
      messageFontSize: to(messageFs),
      gap: to(gap),
      icon: to(icon),
      closeSize: to(closeSize),
      closeFontSize: to(closeFont),
      spinner: to(spinner),
      stripWidth: to(stripWidth),
      minHeight: to(minHeight),
    };
  }

  /**
   * Lit order: willUpdate → controllers hostUpdate (presets) → update/render.
   * Rebuild CSS vars here so preset-applied color/variant are visible.
   */
  override update(changedProperties: PropertyValues): void {
    if (
      changedProperties.has("color") ||
      changedProperties.has("variant") ||
      changedProperties.has("stripColor") ||
      changedProperties.has("radius") ||
      changedProperties.has("size") ||
      changedProperties.has("preset") ||
      this._presets.revision !== this._lastPresetRevision
    ) {
      this._lastPresetRevision = this._presets.revision;
      this._updateStyleVars();
    }
    super.update(changedProperties);
  }

  private _lastPresetRevision = -1;

  /** Initializes actions-slot presence state after first render. */
  override firstUpdated(): void {
    queueMicrotask(() => this._checkActionsSlot());
  }

  /** Computes CSS custom properties from current variant, size, and color inputs. */
  private _updateStyleVars(): void {
    const baseColor = resolveColor(this.color);
    const textColor = ON_COLOR_FALLBACK_LIGHT;
    const stripColorResolved = this.stripColor === "currentColor"
      ? baseColor
      : resolveColor(this.stripColor);

    const s = this._resolveSize(this.size);

    const semanticKey = resolveSemanticSurfaceKey(this.color);

    const variantStyles = {
      solid: {
        bg: baseColor,
        color: textColor,
        border: "none",
      },
      flat: semanticKey
        ? semanticSoftSurfaceTokens(semanticKey)
        : {
            bg: fadeMix(baseColor, 20),
            color: hoverShade(baseColor, 30),
            border: "none",
          },
      faded: semanticKey
        ? semanticSoftSurfaceTokens(semanticKey, { bordered: true })
        : {
            bg: "var(--sky-hover-tertiary)",
            color: baseColor,
            border: "1px solid var(--sky-text-tertiary)",
          },
      bordered: {
        bg: "transparent",
        color: semanticKey
          ? `var(--sky-${semanticKey}-primary-dark)`
          : baseColor,
        border: `2px solid ${baseColor}`,
      },
      light: {
        bg: "transparent",
        color: semanticKey
          ? `var(--sky-${semanticKey}-primary-dark)`
          : baseColor,
        border: "none",
      },
    } as const;

    const currentVariant = this.variant in variantStyles
      ? this.variant as keyof typeof variantStyles
      : "flat";

    let v = tuneSurfaceVariantForDefaultColor(
      this.color,
      currentVariant,
      variantStyles[currentVariant],
    );

    if (
      isDefaultColorToken(this.color) &&
      (currentVariant === "flat" || currentVariant === "faded")
    ) {
      v = {
        ...v,
        bg: "var(--sky-glass-solid)",
        color: "var(--sky-text-primary)",
        border: currentVariant === "faded" ? skyBorderPrimaryShorthand() : "none",
      };
    }

    let closeBg = "var(--sky-hover-primary)";
    let closeColor = "currentColor";
    let closeHoverBg = "var(--sky-hover-secondary)";
    let closeHoverColor = "currentColor";

    if (currentVariant === "solid") {
      closeBg = fadeMix(textColor, 20);
      closeColor = textColor;
      closeHoverBg = fadeMix(textColor, 30);
      closeHoverColor = textColor;
    } else if (currentVariant === "flat" || currentVariant === "faded") {
      if (semanticKey) {
        closeBg = `var(--sky-${semanticKey}-g0)`;
        closeColor = v.color;
        closeHoverBg = `var(--sky-${semanticKey}-g1)`;
        closeHoverColor = v.color;
      } else if (isDefaultColorToken(this.color)) {
        closeBg = "var(--sky-hover-primary)";
        closeColor = v.color;
        closeHoverBg = "var(--sky-hover-secondary)";
        closeHoverColor = v.color;
      } else {
        closeBg = fadeMix(baseColor, 15);
        closeColor = v.color;
        closeHoverBg = fadeMix(baseColor, 25);
        closeHoverColor = v.color;
      }
    }

    this._styleVars = {
      "--alert-bg": v.bg,
      "--alert-color": v.color,
      "--alert-border": v.border,
      "--alert-radius": resolveRadius(this.radius),
      "--alert-padding": s.padding,
      "--alert-gap": s.gap,
      "--alert-min-height": s.minHeight,
      "--title-font-size": s.titleFontSize,
      "--message-font-size": s.messageFontSize,
      "--icon-size": s.icon,
      "--close-size": s.closeSize,
      "--close-font-size": s.closeFontSize,
      "--spinner-size": s.spinner,
      "--strip-color": stripColorResolved,
      "--strip-width": s.stripWidth,
      "--close-bg": closeBg,
      "--close-color": closeColor,
      "--close-hover-bg": closeHoverBg,
      "--close-hover-color": closeHoverColor,
      "--sky-focus-ring-color": baseColor,
    };
  }

  /**
   * Dispatches `alert-closed` and removes the alert from the DOM.
   *
   * @returns {void}
   */
  public close(): void {
    const evt = new CustomEvent<SkyAlertClosedEventDetail>("alert-closed", {
      detail: { title: this.title, message: this.message },
      bubbles: true,
      composed: true,
    });
    this.dispatchEvent(evt);
    this.remove();
  }

  /** Returns the ARIA role applied to the alert container. */
  private getAriaRole(): SkyAlertRoleType {
    return this.roleType;
  }

  /** Checks whether the actions slot currently has meaningful assigned content. */
  private _checkActionsSlot(): void {
    const slot = this.renderRoot?.querySelector<HTMLSlotElement>('slot[name="actions"]');
    if (!slot) return;
    const nodes = slot.assignedNodes({ flatten: true });
    const hasContent = nodes.some(
      (n) => n.nodeType !== Node.TEXT_NODE || (n.textContent?.trim().length ?? 0) > 0,
    );
    if (hasContent !== this._hasActionsSlotContent) {
      this._hasActionsSlotContent = hasContent;
    }
  }

  /** Updates action-slot state when the slot assignment changes. */
  private _onActionsSlotChange(e: Event): void {
    const slot = e.target as HTMLSlotElement;
    const nodes = slot.assignedNodes({ flatten: true });
    const hasContent = nodes.some(
      (n) => n.nodeType !== Node.TEXT_NODE || (n.textContent?.trim().length ?? 0) > 0,
    );
    this._hasActionsSlotContent = hasContent;
  }

  override render() {
    const roleAttr = this.getAriaRole();
    const alertClasses = classMap({
      alert: true,
      [this.variant]: true,
      "strip-start": this.strip === "start" || this.strip === "both",
      "strip-end": this.strip === "end" || this.strip === "both",
    });

    return html`
      <div
        part="alert"
        class=${alertClasses}
        role="${roleAttr}"
        aria-live="${this.roleType === "alert" ? "assertive" : "polite"}"
        style=${styleMap(this._styleVars)}
      >
        ${when(
          this.icon,
          () => html`<sky-icon part="icon" class="icon" icon="${this.icon!}"></sky-icon>`,
        )}

        <div part="content" class="content">
          ${when(
            this.title,
            () => html`<div part="title" class="title">${this.title}</div>`,
          )}
          ${when(
            this.message,
            () => html`<p part="message" class="message">${this.message}</p>`,
            () => html`<slot></slot>`,
          )}
        </div>

        ${when(
          this._hasActionsSlotContent || this.removable,
          () => html`
            <div part="actions" class="actions">
              <slot name="actions" @slotchange=${this._onActionsSlotChange}></slot>
              ${when(
                this.removable,
                () => html`
                  <button
                    part="close-button"
                    class="close-button"
                    type="button"
                    @click=${this.close}
                    aria-label="Dismiss alert"
                    title="Dismiss"
                  >
                    <sky-icon icon="ion:close"></sky-icon>
                  </button>
                `,
              )}
            </div>
          `,
          () => html`
            <div class="actions-slot-placeholder" aria-hidden="true">
              <slot name="actions" @slotchange=${this._onActionsSlotChange}></slot>
            </div>
          `,
        )}

        ${when(
          this.loading,
          () => html`
            <div part="loading-overlay" class="loading-overlay">
              <div part="loading-spinner" class="loading-spinner"></div>
            </div>
          `,
        )}
      </div>
    `;
  }
}